import React, { useState } from 'react';
import { Check, Trophy, Book, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
type PlanId = 'standard' | 'elite';
interface PricingBlockProps {
  sectionId?: string;
}
const CHECKOUT_URLS: Record<PlanId, string> = {
  standard: 'https://checkout.activadigital.store/cart/53261597212966:1',
  elite: 'https://checkout.activadigital.store/cart/53261605634342:1'
};
export function PricingBlock({
  sectionId
}: PricingBlockProps) {
  const [selected, setSelected] = useState<PlanId>('elite');
  const handleCheckout = () => {
    window.open(CHECKOUT_URLS[selected], '_blank', 'noopener,noreferrer');
  };
  const isStandard = selected === 'standard';
  const isElite = selected === 'elite';
  return <section id={sectionId} className="py-8 md:py-12 bg-white" data-id="element-244">
      <div className="max-w-4xl mx-auto px-4" data-id="element-245">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-6 md:mb-8" data-id="element-246">
          <h2 className="text-xl md:text-3xl font-black text-brand-950 mb-1.5 md:mb-2" data-id="element-247">
            Elegí tu Profesorado Resuelto:
          </h2>
          <p className="text-xs md:text-base text-gray-600 font-medium" data-id="element-248">
            Las dos opciones incluyen los 6 bonos estratégicos completos
          </p>
        </motion.div>

        <div className="grid grid-cols-2 gap-2 md:gap-4 mb-5 md:mb-6" data-id="element-249">
          {/* Standard Card */}
          <button type="button" onClick={() => setSelected('standard')} aria-pressed={isStandard} className={`relative text-left bg-white rounded-xl md:rounded-2xl p-3 md:p-5 border-2 transition-all duration-200 ${isStandard ? 'border-brand-600 ring-2 md:ring-4 ring-brand-100 shadow-lg scale-[1.01]' : 'border-gray-200 hover:border-gray-300 opacity-90 hover:opacity-100'}`} data-id="element-250">
            <div className="absolute top-2 right-2 md:top-3 md:right-3" data-id="element-251">
              <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isStandard ? 'border-brand-600 bg-brand-600' : 'border-gray-300 bg-white'}`} data-id="element-252">
                {isStandard && <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white" data-id="element-253"></div>}
              </div>
            </div>

            <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3 pr-5" data-id="element-254">
              <div className="bg-brand-100 p-1 md:p-1.5 rounded-md md:rounded-lg" data-id="element-255">
                <Book className="w-3 h-3 md:w-4 md:h-4 text-brand-600" data-id="element-256" />
              </div>
              <p className="text-[9px] md:text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-257">
                ESTÁNDAR
              </p>
            </div>

            <h3 className="text-xs md:text-base font-black text-brand-900 mb-2 md:mb-3 leading-tight" data-id="element-258">
              PROFESORADO RESUELTO
            </h3>

            <div className="mb-2 md:mb-3 pb-2 md:pb-3 border-b border-gray-100" data-id="element-259">
              <p className="text-gray-400 line-through font-medium text-[10px] md:text-xs" data-id="element-260">
                $49.990
              </p>
              <div className="flex items-baseline gap-0.5 md:gap-1" data-id="element-261">
                <span className="text-lg md:text-2xl font-black text-gray-900" data-id="element-262">
                  $19.990
                </span>
                <span className="text-gray-500 font-bold text-[10px] md:text-xs" data-id="element-263">
                  ARS
                </span>
              </div>
            </div>

            <ul className="space-y-1 md:space-y-1.5" data-id="element-264">
              {['6 Tomos Completos', '6 Bonos Estratégicos', 'Acceso de por vida', 'Actualizaciones'].map((feature, i) => <li key={i} className="flex items-start gap-1 md:gap-1.5" data-id="element-265">
                  <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-brand-500 flex-shrink-0 mt-0.5" data-id="element-266" />
                  <span className="text-gray-700 font-medium text-[10px] md:text-sm leading-tight" data-id="element-267">
                    {feature}
                  </span>
                </li>)}
            </ul>
          </button>

          {/* Elite Card */}
          <button type="button" onClick={() => setSelected('elite')} aria-pressed={isElite} className={`relative text-left bg-brand-900 rounded-xl md:rounded-2xl p-3 md:p-5 border-2 transition-all duration-200 ${isElite ? 'border-accent-500 ring-2 md:ring-4 ring-accent-200 shadow-xl scale-[1.02]' : 'border-brand-700 hover:border-brand-500 opacity-90 hover:opacity-100'}`} data-id="element-268">
            <div className="absolute -top-2.5 md:-top-3 left-1/2 -translate-x-1/2 bg-accent-500 text-white px-2 py-0.5 md:px-2.5 md:py-0.5 rounded-full text-[9px] md:text-xs font-black tracking-wider shadow-lg whitespace-nowrap" data-id="element-269">
              🔥 MÁS ELEGIDO
            </div>

            <div className="absolute top-2 right-2 md:top-3 md:right-3" data-id="element-270">
              <div className={`w-4 h-4 md:w-5 md:h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isElite ? 'border-accent-500 bg-accent-500' : 'border-brand-600 bg-brand-800'}`} data-id="element-271">
                {isElite && <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-white" data-id="element-272"></div>}
              </div>
            </div>

            <div className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3 mt-1 pr-5" data-id="element-273">
              <div className="bg-brand-800 p-1 md:p-1.5 rounded-md md:rounded-lg border border-brand-700" data-id="element-274">
                <Trophy className="w-3 h-3 md:w-4 md:h-4 text-yellow-400" data-id="element-275" />
              </div>
              <p className="text-[9px] md:text-xs font-bold text-brand-300 uppercase tracking-wider" data-id="element-276">
                ÉLITE
              </p>
            </div>

            <h3 className="text-xs md:text-base font-black text-white mb-2 md:mb-3 leading-tight" data-id="element-277">
              KIT COMPLETO ÉLITE™
            </h3>

            <div className="mb-2 md:mb-3 pb-2 md:pb-3 border-b border-brand-700" data-id="element-278">
              <p className="text-brand-400 line-through font-medium text-[10px] md:text-xs" data-id="element-279">
                $59.990
              </p>
              <div className="flex items-baseline gap-0.5 md:gap-1" data-id="element-280">
                <span className="text-lg md:text-2xl font-black text-white" data-id="element-281">
                  $29.990
                </span>
                <span className="text-brand-300 font-bold text-[10px] md:text-xs" data-id="element-282">
                  ARS
                </span>
              </div>
            </div>

            <ul className="space-y-1 md:space-y-1.5" data-id="element-283">
              <li className="flex items-start gap-1 md:gap-1.5" data-id="element-284">
                <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-accent-400 flex-shrink-0 mt-0.5" data-id="element-285" />
                <span className="text-white font-bold text-[10px] md:text-sm leading-tight" data-id="element-286">
                  Todo lo del Estándar
                </span>
              </li>
              <li className="flex items-start gap-1 md:gap-1.5" data-id="element-287">
                <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-accent-400 flex-shrink-0 mt-0.5" data-id="element-288" />
                <span className="text-brand-100 font-medium text-[10px] md:text-sm leading-tight" data-id="element-289">
                  Kit de Recursos Visuales
                </span>
              </li>
              <li className="flex items-start gap-1 md:gap-1.5" data-id="element-290">
                <Check className="w-3 h-3 md:w-3.5 md:h-3.5 text-accent-400 flex-shrink-0 mt-0.5" data-id="element-291" />
                <span className="text-brand-100 font-medium text-[10px] md:text-sm leading-tight" data-id="element-292">
                  Guías Visuales 5 Materias
                </span>
              </li>
            </ul>
          </button>
        </div>

        <div className="max-w-md mx-auto" data-id="element-293">
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} onClick={handleCheckout} className="w-full bg-accent-500 hover:bg-accent-600 text-white font-black text-sm md:text-lg py-3 md:py-4 px-4 md:px-6 rounded-xl shadow-[0_8px_24px_rgb(239,68,68,0.35)] flex items-center justify-center gap-1.5 md:gap-2" data-id="element-294">
            💥 QUIERO MI{' '}
            {isElite ? 'KIT COMPLETO ÉLITE' : 'PROFESORADO RESUELTO'}
            <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-295" />
          </motion.button>

          <p className="text-center text-gray-500 text-[10px] md:text-xs mt-2 md:mt-3 font-medium" data-id="element-296">
            Pago seguro · Acceso inmediato · Garantía 7 días
          </p>

          <p className="text-center text-gray-400 text-[9px] md:text-[11px] mt-1 md:mt-2" data-id="element-297">
            Plan seleccionado:{' '}
            <span className="font-bold text-brand-700" data-id="element-298">
              {isElite ? 'Kit Completo Élite — $29.990 ARS' : 'Estándar — $19.990 ARS'}
            </span>
          </p>
        </div>
      </div>
    </section>;
}